import { string as zString } from 'zod';
import { ga, pushDataLayer } from '@hungryroot/google-analytics';
import {
  StatusCode,
  postFacebookLogin,
  type SignupResponse,
} from '@hungryroot/rest';
import { funnelAnalytics } from '@hungryroot/segment';
import { logSso } from './o11y';
import { loadScript } from './provider';

const FACEBOOK_APP_ID = zString().parse(process.env.PUBLIC_FACEBOOK_APP_ID);
const SCRIPT_SRC = 'https://connect.facebook.net/en_US/sdk.js';

type Params = {
  onReady: (sdk: fb.FacebookStatic) => void;
};

export async function loadFacebookSdk({ onReady }: Params) {
  if (window.FB !== undefined) {
    onReady(window.FB);
    return;
  }

  window.fbAsyncInit = function () {
    FB.init({
      appId: FACEBOOK_APP_ID,
      cookie: true,
      status: true,
      autoLogAppEvents: true,
      xfbml: true,
      version: 'v6.0',
    });

    onReady(FB);
  };

  loadScript({ src: SCRIPT_SRC, globalProperty: 'FB' });
}

type AuthParams = {
  onLoginSuccess: (data: SignupResponse) => void;
  onSignupSuccess: (data: SignupResponse) => void;
  onError: (data: unknown) => void;
  fb: Pick<fb.FacebookStatic, 'login'>;
  /** Marks a freemium (guest-flow) signup if this auth creates an account. */
  isGuestFlow?: boolean;
};

export function facebookAuth({
  onError,
  onSignupSuccess,
  onLoginSuccess,
  fb,
  isGuestFlow,
}: AuthParams) {
  const segment = funnelAnalytics();

  logSso({
    provider: 'facebook',
    lifecycle: 'initiated',
  });
  fb.login(
    (resp) => {
      const accessToken = resp.authResponse?.accessToken;
      const userID = resp.authResponse?.userID;
      const errors: string[] = [];
      if (resp.status !== 'connected') {
        logSso({
          provider: 'facebook',
          lifecycle: 'error',
          error: {
            status: resp.status,
          },
        });
        errors.push('Bad Facebook user ID');
      }

      if (accessToken === undefined) {
        logSso({
          provider: 'facebook',
          lifecycle: 'error',
          error: {
            status: 'undefined_access_token',
          },
        });

        errors.push('Bad Facebook token ');
      }

      if (errors.length > 0) {
        onError(errors);
        return;
      }

      postFacebookLogin({
        userId: userID,
        token: accessToken as string,
        isGuestFlow,
      }).then((response) => {
        if (response.status !== StatusCode.SuccessOK) {
          ga('send', 'event', 'login', 'failure');
          logSso({
            provider: 'facebook',
            lifecycle: 'error',
            error: {
              message: 'api_facebook_login_failed',
              statusCode: response.status,
            },
          });
          onError('Bad server response');
          return;
        }

        const {
          data: { customer, created },
        } = response;
        const eventType = created === true ? 'signup' : 'login';
        ga('send', 'event', eventType, 'success');
        pushDataLayer({
          event: `hr.${eventType}.success`,
          [eventType]: {
            method: 'facebook',
          },
          customer: {
            id: customer.id,
            email: customer.email,
          },
        });

        logSso({
          provider: 'facebook',
          lifecycle: 'success',
          eventType,
        });

        if (created === true) {
          segment.completedRegistration(customer, 'facebook');
          onSignupSuccess(customer);
          return;
        }

        onLoginSuccess(customer);
      });
    },
    { scope: 'public_profile,email', return_scopes: true }
  );
}
